<template>
  <div :style="styles" :class="classes">
    <img
      v-if="is404"
      src="/assets/images/smart-banner/error-404-right-decorator.png"
      alt=""
      class="smart-banner__decorator smart-banner__decorator--right"
    />
    <img
      v-if="hasDecorator"
      src="/assets/images/smart-banner/sb-decorator-02.png"
      alt=""
      class="smart-banner__defaultDecorator smart-banner__defaultDecorator--right"
    />
    <div :class="['smart-banner__content', { reduceWidth: reduceWidth }]">
      <img
        v-if="is404"
        src="/assets/images/smart-banner/error-404.png"
        alt=""
        class="smart-banner__error"
      />
      <c-kicker
        v-if="hasKicker"
        :color="kickerColor"
        :weight="kickerWeight"
        :align="kickerAlign"
        class="smart-banner__kicker"
      >
        <slot name="kicker" />
      </c-kicker>
      <c-title
        :color="titleColor"
        :size="titleSize"
        :weight="titleWeight"
        :align="titleAlign"
      >
        <slot name="title" />
      </c-title>
      <c-text
        :color="textColor"
        :size="textSize"
        :weight="textWeight"
        :align="textAlign"
        class="smart-banner__text"
      >
        <slot name="text" />
      </c-text>
      <div
        v-if="hasButton"
        :class="['smart-banner__buttons', { centerItems: centerItems }]"
      >
        <slot name="button" />
      </div>
    </div>
    <div v-if="hasImage || hasFixedImage" class="smart-banner__image">
      <c-image
        :image-mobile="imageMobile"
        :image-tablet="imageTablet"
        :image-desktop="imageDesktop"
        :alt="alt"
        :class="{ 'smart-banner__fixedImage': hasFixedImage }"
      />
    </div>
    <div v-if="hasVideo" class="smart-banner__video">
      <slot name="video" />
    </div>
    <img
      v-if="is404"
      src="/assets/images/smart-banner/error-404-left-decorator.png"
      alt=""
      class="smart-banner__decorator smart-banner__decorator--left"
    />
    <img
      v-if="hasDecorator"
      src="/assets/images/smart-banner/sb-decorator-01.png"
      alt=""
      class="smart-banner__defaultDecorator smart-banner__defaultDecorator--left"
    />
  </div>
</template>

<script>
import CKicker from '../../02_atoms/kicker/kicker.vue'
import CTitle from '../../00_v2/title/title.vue'
import CText from '../../00_v2/text/text.vue'
import CImage from '../../00_v2/image/image.vue'

export default {
  name: 'smart-banner',
  components: {
    CKicker,
    CTitle,
    CText,
    CImage,
  },
  props: {
    bgColor: {
      type: String,
    },
    direction: {
      type: String,
    },
    hasKicker: {
      type: Boolean,
      default: false,
    },
    kickerColor: {
      type: String,
      default: '#717D96',
    },
    kickerWeight: {
      type: String,
      default: '700',
    },
    kickerAlign: {
      type: String,
    },
    titleColor: {
      type: String,
    },
    titleSize: {
      type: String,
      default: 'h2',
    },
    titleWeight: {
      type: String,
      default: '400',
    },
    titleAlign: {
      type: String,
    },
    textColor: {
      type: String,
    },
    textSize: {
      type: String,
      default: 'xs',
    },
    textWeight: {
      type: String,
      default: '400',
    },
    textAlign: {
      type: String,
    },
    hasButton: {
      type: Boolean,
      default: false,
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    imageMobile: {
      type: String,
    },
    imageTablet: {
      type: String,
    },
    imageDesktop: {
      type: String,
    },
    alt: {
      type: String,
    },
    centerItems: {
      type: Boolean,
      default: false,
    },
    reduceWidth: {
      type: Boolean,
      default: false,
    },
    hasVideo: {
      type: Boolean,
      default: false,
    },
    is404: {
      type: Boolean,
      default: false,
    },
    hasDecorator: {
      type: Boolean,
      default: false,
    },
    hasFixedImage: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    classes() {
      return [
        'smart-banner',
        `smart-banner--${this.direction}`,
        this.is404 ? 'smart-banner--is404' : '',
        this.hasDecorator ? 'smart-banner--defaultDecorator' : '',
        this.hasFixedImage ? 'smart-banner--fixedImage' : '',
      ]
    },
    styles() {
      return {
        backgroundColor: this.bgColor,
      }
    },
  },
}
</script>

<style lang="scss">
.smart-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;

  &__text {
    display: block !important;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &.reduceWidth {
      padding: 2rem;

      @media (min-width: 768px) {
        padding: 2rem 4rem;
      }

      @media (min-width: 1200px) {
        padding: 2rem 12rem;
      }

      @media screen and (min-width: 1512px) {
        padding: 2rem 17rem;
      }
    }
  }

  &__content,
  &__image,
  &__video {
    flex: 1;
  }

  &__video {
    display: contents;
    overflow: hidden;

    @media (min-width: 1200px) {
      display: unset;
    }
  }

  &__fixedImage {
    display: none;

    @media (min-width: 1200px) {
      display: block;
      width: 37.5rem !important;
      position: relative;
      bottom: 22rem;
      left: 12rem;
    }

    @media (min-width: 1400px) {
      left: 20rem;
    }
  }

  &--fixedImage {
    align-items: start;
    overflow: hidden;
    padding-inline: 2rem;

    @media (min-width: 1200px) {
      height: 18rem;
    }

    @media (min-width: 1400px) {
      padding-left: 9rem;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin-top: 1.5rem;
    gap: 1rem;

    &.centerItems {
      display: flex;
      flex-direction: column;
      justify-content: center;

      @media (min-width: 768px) {
        flex-direction: row;
      }
    }

    @media (min-width: 375px) {
      flex-direction: row;
      align-items: center;
    }
  }

  &__defaultDecorator,
  &__decorator {
    display: none;

    @media (min-width: 1200px) {
      display: block;
      position: relative;

      &--left {
        right: 6rem;
        bottom: 10rem;
      }

      &--right {
        left: 6rem;
      }
    }
  }

  &__decorator {
    @media (min-width: 1200px) {
      width: 33%;
      top: 16.5rem;

      &--left {
        bottom: unset;
      }
    }

    @media (min-width: 1400px) {
      width: 35%;
    }
  }

  &__error {
    width: 6rem;
    margin: 0 auto;
    padding-bottom: 1rem;
  }

  &--is404 {
    @extend .smart-banner--defaultDecorator;
    @media (min-width: 1200px) {
      height: 37.5rem;
    }
  }

  &--defaultDecorator {
    overflow: hidden;
    gap: unset;
  }
}
</style>
